import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { EmailThreadList, type EmailThread } from "./EmailThreadList";
import { EmailDetailPanel } from "./EmailDetailPanel";
import { NewEmailComposeModal, type InquiryForEmail } from "./NewEmailComposeModal";

interface EmailInboxProps {
  navigateToEmail?: {
    email: string;
    inquiry: InquiryForEmail;
  } | null;
  onNavigationHandled?: () => void;
}

export function EmailInbox({ navigateToEmail, onNavigationHandled }: EmailInboxProps) {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Compose modal state
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [composeInquiry, setComposeInquiry] = useState<InquiryForEmail | null>(null);
  
  // Use ref to access selectedThread without causing re-renders
  const selectedThreadIdRef = useRef<string | null>(null);
  selectedThreadIdRef.current = selectedThread?.id || null;

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      // Build query
      let query = supabase
        .from("email_threads")
        .select(`
          id,
          sender_name,
          sender_email,
          subject,
          status,
          unread_count,
          last_message_at,
          is_archived,
          inquiry_id,
          inquiry:inquiries (
            first_name,
            last_name,
            email,
            phone,
            wedding_date,
            venue,
            services,
            budget,
            message,
            selected_package
          )
        `)
        .order("last_message_at", { ascending: false });

      // Apply filters
      if (statusFilter === "archived") {
        query = query.eq("is_archived", true);
      } else if (statusFilter !== "all") {
        query = query.eq("status", statusFilter).eq("is_archived", false);
      } else {
        query = query.eq("is_archived", false);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch latest email preview for each thread
      const threadsWithPreviews = await Promise.all(
        (data || []).map(async (thread) => {
          const { data: latestEmail } = await supabase
            .from("emails")
            .select("body_text")
            .eq("thread_id", thread.id)
            .order("sent_at", { ascending: false })
            .limit(1)
            .single();

          return {
            ...thread,
            inquiry: Array.isArray(thread.inquiry) ? thread.inquiry[0] : thread.inquiry,
            latest_email_preview: latestEmail?.body_text?.substring(0, 80) || undefined,
          } as EmailThread;
        })
      );

      // Apply search filter client-side
      let filtered = threadsWithPreviews;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        filtered = threadsWithPreviews.filter(
          (t) =>
            t.sender_name?.toLowerCase().includes(q) ||
            t.sender_email?.toLowerCase().includes(q) ||
            t.subject.toLowerCase().includes(q) ||
            t.latest_email_preview?.toLowerCase().includes(q)
        );
      }

      setThreads(filtered);

      // Update selected thread if it's still in the list (using ref to avoid dependency)
      const currentSelectedId = selectedThreadIdRef.current;
      if (currentSelectedId) {
        const updated = filtered.find((t) => t.id === currentSelectedId);
        if (updated) {
          setSelectedThread(updated);
        } else {
          setSelectedThread(null);
        }
      }
      
      return filtered;
    } catch (error) {
      console.error("Error fetching threads:", error);
      toast({ title: "Failed to load emails", variant: "destructive" });
      return [];
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  // Handle navigation from Inquiries tab
  useEffect(() => {
    if (navigateToEmail && !loading) {
      const { email, inquiry } = navigateToEmail;
      
      // Find thread matching the email
      const matchingThread = threads.find(
        (t) => 
          t.sender_email?.toLowerCase() === email.toLowerCase() ||
          t.inquiry?.email?.toLowerCase() === email.toLowerCase()
      );
      
      if (matchingThread) {
        // Thread found - select it
        setSelectedThread(matchingThread);
      } else {
        // No thread - open compose modal
        setComposeInquiry(inquiry);
        setComposeModalOpen(true);
      }
      
      // Clear navigation state
      onNavigationHandled?.();
    }
  }, [navigateToEmail, threads, loading, onNavigationHandled]);

  useEffect(() => {
    fetchThreads();

    // Set up realtime subscription
    const channel = supabase
      .channel("email-threads-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "email_threads" },
        () => fetchThreads()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "emails" },
        () => fetchThreads()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchThreads]);

  const handleArchive = async (threadId: string) => {
    try {
      const { error } = await supabase
        .from("email_threads")
        .update({ is_archived: true })
        .eq("id", threadId);

      if (error) throw error;
      toast({
        title: "Thread archived",
        description: "The conversation has been moved to archive",
      });
      setSelectedThread(null);
      fetchThreads();
    } catch (error) {
      console.error("Error archiving thread:", error);
      toast({ title: "Failed to archive", variant: "destructive" });
    }
  };

  const handleDelete = async (threadId: string) => {
    try {
      // Delete emails first
      await supabase.from("emails").delete().eq("thread_id", threadId);
      // Then delete thread
      const { error } = await supabase
        .from("email_threads")
        .delete()
        .eq("id", threadId);

      if (error) throw error;
      toast({ title: "Thread deleted" });
      setSelectedThread(null);
      fetchThreads();
    } catch (error) {
      console.error("Error deleting thread:", error);
      toast({ title: "Failed to delete", variant: "destructive" });
    }
  };

  const handleComposeEmailSent = () => {
    setComposeModalOpen(false);
    setComposeInquiry(null);
    fetchThreads();
  };

  return (
    <div className="h-[calc(100vh-12rem)] flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="text-lg font-semibold">Email Inbox</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setComposeModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Email
          </Button>
          <Button variant="outline" size="sm" onClick={fetchThreads}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="flex-1 flex overflow-hidden">
        <div className="w-[380px] flex-shrink-0">
          <EmailThreadList
            threads={threads}
            selectedThreadId={selectedThread?.id || null}
            onSelectThread={setSelectedThread}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            loading={loading}
          />
        </div>
        <EmailDetailPanel
          thread={selectedThread}
          onThreadUpdate={fetchThreads}
          onThreadArchive={handleArchive}
          onThreadDelete={handleDelete}
        />
      </div>

      {/* Compose modal for new emails */}
      <NewEmailComposeModal
        open={composeModalOpen}
        onOpenChange={setComposeModalOpen}
        inquiry={composeInquiry}
        onEmailSent={handleComposeEmailSent}
      />
    </div>
  );
}
